import { useState, useEffect, useCallback } from 'react';
import Landing from '../components/landing';
import Info from '../components/info';
import Form from '../components/form/form';
import ArtSection from '../components/art';
import Footer from '../components/footer';
import { twelveDataAPI } from '../api/twelve-data';
import { coingeckoAPI } from '../api/coingecko';
import logo from '../assets/images/logo.png';

const normalizeTwelveData = (data) => ({
    meta: data.meta,
    values: data.values.map((v) => ({
        datetime: v.datetime,
        open: parseFloat(v.open),
        high: parseFloat(v.high),
        low: parseFloat(v.low),
        close: parseFloat(v.close),
        volume: v.volume ? parseFloat(v.volume) : null,
    })),
    status: data.status,
});

const detectCoinGeckoInterval = (data) => {
    if (data.length < 2) return '1day';
    const daysDiff = (data[1][0] - data[0][0]) / (1000 * 60 * 60 * 24);
    if (daysDiff >= 3.5) return '4day';
    if (daysDiff >= 0.9) return '1day';
    if (daysDiff >= 0.15) return '4hour';
    return '30min';
};

const DAILY_OR_COARSER = new Set(['1D', '1W', '1M']);

const aggregateToDailyOHLC = (values) => {
    const byDate = {};
    for (const candle of values) {
        if (!byDate[candle.datetime]) {
            byDate[candle.datetime] = { ...candle };
        } else {
            byDate[candle.datetime].high = Math.max(
                byDate[candle.datetime].high,
                candle.high
            );
            byDate[candle.datetime].low = Math.min(
                byDate[candle.datetime].low,
                candle.low
            );
            byDate[candle.datetime].close = candle.close;
        }
    }
    return Object.values(byDate);
};

const buildCoinGeckoWarnings = (originalInterval, finalInterval) => {
    const warnings = [
        'Twelve Data unavailable — price data sourced from CoinGecko. Volume data is not included.',
    ];
    if (originalInterval !== finalInterval) {
        warnings.push(
            `CoinGecko returned ${originalInterval} candles — aggregated to daily to match your selected timeframe.`
        );
    } else if (finalInterval === '4day') {
        warnings.push(
            'With CoinGecko, date ranges over 30 days are limited to 4-day candles. Select a range within the last 30 days for daily candles.'
        );
    } else if (finalInterval === '4hour') {
        warnings.push(
            'CoinGecko returned 4-hour candles for this date range. Select a shorter range for daily candles.'
        );
    }
    return warnings;
};

const normalizeCoinGeckoOHLC = (data, asset, dateRange) => {
    const interval = detectCoinGeckoInterval(data);
    return {
        meta: {
            symbol: asset.symbol,
            interval,
            currency: 'USD',
            source: 'coingecko',
        },
        values: data
            .map(([ts, open, high, low, close]) => ({
                datetime: new Date(ts).toISOString().split('T')[0],
                open,
                high,
                low,
                close,
                volume: null,
            }))
            .filter(
                (v) =>
                    v.datetime >= dateRange.start && v.datetime <= dateRange.end
            ),
        status: 'ok',
    };
};

const Home = () => {
    const [submission, setSubmission] = useState(null);
    const [chartData, setChartData] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [apiError, setApiError] = useState(null);

    const fetchChartData = useCallback(async (formData) => {
        setIsSubmitting(true);
        setApiError(null);
        setChartData(null);

        try {
            const result = await twelveDataAPI.getTimeSeries({
                asset: formData.asset,
                interval: formData.timeframe,
                startDate: formData.dateRange.start,
                endDate: formData.dateRange.end,
            });

            if (!result.values?.length) throw new Error('No data returned');

            const twelveNormalized = normalizeTwelveData(result);
            const warnings = [];
            if (twelveNormalized.values.length >= 5000) {
                warnings.push(
                    'Twelve Data returned the maximum of 5,000 candles — your date range likely contains more data than shown. Try a shorter date range or a larger timeframe interval.'
                );
            }
            setSubmission(formData);
            setChartData({ ...twelveNormalized, source: 'twelve-data', warnings });
        } catch {
            if (
                formData.asset.type === 'crypto' &&
                formData.asset.coingeckoId
            ) {
                try {
                    const VALID_DAYS = [1, 7, 14, 30, 90, 180, 365];
                    const msPerDay = 1000 * 60 * 60 * 24;
                    const daysNeeded = Math.ceil(
                        (Date.now() -
                            new Date(formData.dateRange.start).getTime()) /
                            msPerDay
                    );
                    const days = VALID_DAYS.find((d) => d >= daysNeeded) ?? 365;

                    const ohlc = await coingeckoAPI.getOHLC(
                        formData.asset.coingeckoId,
                        days
                    );

                    if (!ohlc?.length) throw new Error('No data returned');

                    const coinGeckoNormalized = normalizeCoinGeckoOHLC(
                        ohlc,
                        formData.asset,
                        formData.dateRange
                    );

                    const originalInterval = coinGeckoNormalized.meta.interval;
                    let finalValues = coinGeckoNormalized.values;
                    let finalInterval = originalInterval;

                    if (
                        originalInterval === '4hour' &&
                        DAILY_OR_COARSER.has(formData.timeframe)
                    ) {
                        finalValues = aggregateToDailyOHLC(finalValues);
                        finalInterval = '1day';
                    }

                    const finalNormalized = {
                        ...coinGeckoNormalized,
                        meta: {
                            ...coinGeckoNormalized.meta,
                            interval: finalInterval,
                        },
                        values: finalValues,
                    };

                    const warnings = buildCoinGeckoWarnings(
                        originalInterval,
                        finalInterval
                    );
                    setSubmission(formData);
                    setChartData({
                        ...finalNormalized,
                        source: 'coingecko',
                        warnings,
                    });
                } catch {
                    setApiError(
                        'Unable to load chart data for this asset. Please try again.'
                    );
                }
            } else {
                setApiError('Price data is not available for this asset.');
            }
        } finally {
            setIsSubmitting(false);
        }
    }, []);

    useEffect(() => {
        if (submission) {
            document
                .getElementById('art-section')
                ?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [submission]);

    return (
        <>
        <div className="mobile-block">
            <img src={logo} alt="ChArtIt" />
            <p>ChArtIt is designed for desktop use.</p>
            <em>Please visit on a larger screen to generate your pixel art.</em>
        </div>
        <article className="home">
            <div className="wrapper">
                <Landing />
                <Info />
                <Form
                    onSubmit={fetchChartData}
                    isSubmitting={isSubmitting}
                    apiError={apiError}
                />
                {submission && (
                    <>
                        <div className="fade-to-black" aria-hidden="true" />
                        <ArtSection
                            submission={submission}
                            chartData={chartData}
                        />
                    </>
                )}
                <Footer />
            </div>
        </article>
        </>
    );
};

export default Home;
